import Prism from 'prismjs';
import 'prismjs/components/prism-sql';
import 'prismjs/themes/prism-okaidia.css';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-toml';
import 'prismjs/components/prism-xml-doc';
import 'prismjs/components/prism-properties';
import 'prismjs/components/prism-ini';
import 'prismjs/plugins/line-numbers/prism-line-numbers.min';
import 'prismjs/plugins/line-numbers/prism-line-numbers.min.css';
import 'vue-json-pretty/lib/styles.css';
import {
  CSSProperties,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  toRef,
  watch,
  watchEffect,
} from 'vue';
import type { TextType } from '@/common/components/molecules/textViewer/textViewer.type';
import { useInternational } from '@/common/locale';
import { debounce } from 'lodash-es';
import { useChunkHighlight } from '@/common/components/organisms/sqlFullText/uses';
import { copyTextToClipboard } from '@/common/utils/browser';
import { getJsonFormatted } from '@/common/utils/string.utils';
import { usePersistedToggle } from '@/common/composables/usePersistedToggle';

export interface Option {
  useSeeMore?: boolean;
  useScrollTo?: boolean;
}

export interface Props {
  type?: TextType;
  useLineNumbers?: boolean;
  useBorder?: boolean;
  title?: string;
  text?: string;
  copy?: boolean;
  option?: Option;
  style?: CSSProperties;
  isShow?: boolean;
  noDataMsg?: string;
  useJsonVirtualScroll?: boolean;
  useExpand?: boolean;
  usePrism?: boolean;
  isFormatted?: boolean;
  isExpanded?: boolean;
  expandEnvKey?: string;
  // text가 비어도 라인넘버 프레임을 유지하고 no-data를 라인넘버 칼럼 제외 영역 중앙에 표시
  useLineNumbersOnEmpty?: boolean;
}

type JsonType = object | null;

const isScrollable = (el: Element | undefined) => {
  if (!el) return false;
  const hasScrollableContent = el.scrollHeight > el.clientHeight;
  return hasScrollableContent;
};

export const setup = (props: Required<Props>) => {
  const { t } = useInternational();

  const ctx = getCurrentInstance()!.appContext.config.globalProperties;

  const { isToggled: isExpand } = usePersistedToggle(props.expandEnvKey);
  const onToggleExpand = () => {
    isExpand.value = !isExpand.value;
  };

  const textAreaEl = ref();
  const preWrapper = ref<HTMLPreElement>();

  const jsonObj = ref<any>(null);
  const jsonVirtualScrollOption = reactive({
    height: 400,
    itemHeight: 20,
  });

  const clickCopy = () => {
    if (props.type === 'json') {
      copyTextToClipboard(ctx, getJsonFormatted(jsonObj.value, { space: 2 }) || '');
    } else {
      copyTextToClipboard(ctx, props.text);
    }
  };

  const convertTextToObj = (text: string): JsonType => {
    let result: JsonType;

    try {
      result = JSON.parse(text);
    } catch (e) {
      console.log(e);
      result = null;
    }

    return result;
  };

  const more = ref({
    isExist: false,
    isExpand: true,
    isFold: false,
    text: t('WORD.UI.MONITORING.LOG.MORE'),
  });

  const containerStyle = ref<CSSProperties>({
    height: '',
  });

  const expandMessage = () => {
    if (more.value.isExpand) {
      containerStyle.value = { height: '100%' };
      more.value.isExpand = false;
      more.value.isFold = true;
      more.value.text = t('WORD.LESS');
    } else {
      containerStyle.value = { height: props?.style.height };
      more.value.isExpand = true;
      more.value.isFold = false;
      more.value.text = t('WORD.UI.MONITORING.LOG.MORE');
    }
    if (props.option?.useScrollTo) {
      nextTick(() => {
        const el = preWrapper.value?.closest('.text-viewer');
        el?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    }
  };

  const highLightPrism = debounce(async () => {
    if (props.type === 'json' || !props.isShow || !props.usePrism) {
      return;
    }

    await nextTick();
    Prism.highlightAllUnder(textAreaEl.value ?? document);
  }, 0);

  watch(
    () => props?.style.height,
    (newHeight) => {
      containerStyle.value = { height: newHeight };
    },
    { immediate: true },
  );

  watchEffect(() => {
    more.value.isExist = (props?.option?.useSeeMore ?? false) && isScrollable(preWrapper.value);
    more.value.isExpand = more.value.isExist;
  });

  watch(
    [() => props.type, () => props.text] as const,
    ([type, text]) => {
      if (type === 'json') {
        jsonObj.value = text ? convertTextToObj(text) : null;
      } else {
        highLightPrism();
      }
    },
    { immediate: true },
  );

  watch(
    () => props.isShow,
    () => {
      highLightPrism();
    },
    { immediate: true },
  );

  watch(
    () => props.isExpanded,
    (newVal) => {
      if (newVal && props.useLineNumbers) {
        highLightPrism();
      }
    },
  );

  watch(
    () => isExpand.value,
    (newVal) => {
      if (newVal) {
        highLightPrism();
      }
    },
  );

  const updateJsonVirtualScrollOption = debounce(() => {
    if (props.useJsonVirtualScroll && textAreaEl.value) {
      // NOTE (36 + 12 + 10) = 58 header + padding + horizontal scroll
      jsonVirtualScrollOption.height =
        (textAreaEl.value.getBoundingClientRect().height ?? 400) - 58;
    }
  }, 300);

  const jsonElResizeObserver = new ResizeObserver(() => {
    updateJsonVirtualScrollOption();
  });

  onMounted(() => {
    if (document.documentElement.dataset.theme === 'light') {
      import('prismjs/themes/prism.css');
    }

    if (textAreaEl.value) {
      jsonElResizeObserver.observe(textAreaEl.value as Element);
    }
    updateJsonVirtualScrollOption();
  });

  onBeforeUnmount(() => {
    highLightPrism.cancel();
    jsonElResizeObserver.disconnect();
  });

  const { visibleSqlTextHtmlChunks, onScroll } = useChunkHighlight({
    displaySqlText: toRef(() => props.text),
    fullTextComp: preWrapper,
    useLineNumbers: props.useLineNumbers,
    isFormatted: toRef(() => props.isFormatted),
  });

  return {
    textAreaEl,
    jsonObj,
    jsonVirtualScrollOption,
    preWrapper,
    containerStyle,
    more,
    clickCopy,
    expandMessage,
    t,

    isExpand,
    onToggleExpand,

    onScroll,
    visibleSqlTextHtmlChunks,
  };
};
